import React, { useEffect, useState } from "react";

import { Divider } from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import { Alert } from "@material-ui/lab";
import { Form, Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";

import { RuleAction, RuleBookAction } from "actions";

import { MultiButton } from "components/Button";
import { Button, SecondaryButton } from "components/Form";
import { useSubmitHandler } from "components/Form/FormikControls";
import { DialogActions, DialogContent } from "components/MaterialDialog";

import { ModalTypes } from "constants/navigation";
import { RuleBookPermission } from "constants/permissions";
import { MasterRuleStatus } from "constants/rules";

import { convertToSchemaPath } from "containers/Settings/RuleBooks/import";
import { CustomiseManagedRuleForm } from "containers/Settings/RuleBooks/Rules/ManagedRules";
import { RuleFieldSchema } from "containers/Settings/RuleBooks/Schema/schemaContext";

import { openModalLink } from "lib/navigation";
import { getMasterRuleStatus, SynchronisedRuleFields } from "lib/rules";
import { formatUTCToLocalDateTimeString } from "lib/timeFormats";

import {
  getIsStaff,
  getMasterRuleBookById,
  getMasterRuleById,
  getMasterRuleSetById,
  getRuleBookById,
  getRuleById,
  getRuleSetById,
} from "selectors";

import { useHasPermission } from "hooks";

function Footer(props) {
  const { onClose, ruleBookId } = props;
  const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);

  useSubmitHandler(true, setIsSubmitEnabled);

  const hasUpdatePermission = useHasPermission(
    getRuleBookById(ruleBookId),
    RuleBookPermission.update,
  );

  return (
    <>
      <SecondaryButton type="button" onClick={onClose}>
        Close
      </SecondaryButton>
      <Button
        type="submit"
        disabled={!(isSubmitEnabled && hasUpdatePermission)}
        title={
          !hasUpdatePermission
            ? "You do not have permission to Customise Rules"
            : "Customise Rule"
        }
      >
        Save
      </Button>
    </>
  );
}

function OutOfSyncWarning({ managedRule }) {
  const dispatch = useDispatch();
  const masterRule = useSelector(getMasterRuleById(managedRule.master_rule_id));
  const masterRuleBook = useSelector(
    getMasterRuleBookById(masterRule?.rule_book_id),
  );
  const ruleSet = useSelector(getRuleSetById(managedRule.rule_set_id));
  const masterRuleSet = useSelector(
    getMasterRuleSetById(masterRule?.rule_set_id),
  );
  const hasUpdatePermission = useHasPermission(
    getRuleBookById(managedRule.rule_book_id),
    RuleBookPermission.update,
  );

  if (
    !masterRule ||
    getMasterRuleStatus(managedRule, masterRule, ruleSet, masterRuleSet) !==
      MasterRuleStatus.OUT_SYNC
  ) {
    return null;
  }

  const syncRule = () => {
    const synchronizedValues = SynchronisedRuleFields.reduce(
      (values, field) => {
        values[field] = masterRule[field];
        return values;
      },
      { id: managedRule.id },
    );
    dispatch(
      RuleAction.update(synchronizedValues, {
        changeReason: "Synchronized with Master Rule",
      }),
    );
  };

  const syncMasterRuleBook = () => {
    dispatch(
      RuleBookAction.synchronizeWithMasterRuleBook({
        id: managedRule.rule_book_id,
        masterRuleBookId: masterRule.rule_book_id,
      }),
    );
  };

  const masterRuleLastUpdated = formatUTCToLocalDateTimeString(
    masterRule.last_modified,
  );
  const managedRuleLastUpdated = formatUTCToLocalDateTimeString(
    managedRule.last_modified,
  );

  return (
    <Alert severity="warning">
      <div>
        This rule is not in sync with its master rule. The master rule was last
        updated on {masterRuleLastUpdated}; this rule was last updated on{" "}
        {managedRuleLastUpdated}.
      </div>
      <Grid className="pt-2" container spacing={1}>
        <Grid item>
          <Button
            disabled={!hasUpdatePermission}
            onClick={syncRule}
            type="button"
          >
            Sync This Rule
          </Button>
        </Grid>
        <Grid item>
          <SecondaryButton
            disabled={!hasUpdatePermission}
            onClick={syncMasterRuleBook}
            type="button"
          >
            Sync Entire Master Rule Book &quot;{masterRuleBook?.name}&quot;
          </SecondaryButton>
        </Grid>
      </Grid>
    </Alert>
  );
}

export function CustomiseManagedRuleModalForm(props) {
  const { ruleId, onClose } = props;

  const dispatch = useDispatch();

  useEffect(() => {
    ruleId && dispatch(RuleAction.requestOne({ id: ruleId }));
  }, [dispatch, ruleId]);

  const managedRule = useSelector(getRuleById(ruleId));

  const ruleBookId = managedRule.rule_book_id;

  const initialValues = {
    comment: managedRule.comment,
    gl_code: managedRule.gl_code,
    name: managedRule.name,
    title_template: managedRule.title_template,
    invoice_line_item_template: managedRule.invoice_line_item_template,
    quantity_output_format: managedRule.quantity_output_format,
    quantity_raw_format: managedRule.quantity_raw_format,
    unit_amount_output_format: managedRule.unit_amount_output_format,
    unit_amount_raw_format: managedRule.unit_amount_raw_format,
    separate_document_key: managedRule.separate_document_key,
  };

  function onSubmit(values) {
    dispatch(
      RuleAction.update(
        { ...values, id: ruleId },
        {
          changeReason: "Customised from Rule Modal",
        },
      ),
    );
    onClose();
  }

  function editMasterRule() {
    openModalLink(
      ModalTypes.EditMasterRule,
      { masterRuleId: managedRule.master_rule_id },
      window.location.hash,
    );
  }

  function editRuleBook() {
    openModalLink(ModalTypes.RuleBook, {
      id: managedRule.rule_book_id,
    });
  }

  const isStaff = useSelector(getIsStaff);

  const staffActions = [
    {
      title: "Edit Master Rule",
      onClick: editMasterRule,
      dataTour: "editMasterRule",
    },
    {
      title: "Edit Rule Book",
      onClick: editRuleBook,
      dataTour: "editMasterRule",
    },
  ];

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit}>
      <Form>
        <DialogContent>
          <RuleFieldSchema value={false}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                This rule is managed by AgriNous.
                {isStaff && <MultiButton buttons={staffActions} />}
              </Grid>
              {isStaff && (
                <Grid item xs={12}>
                  <OutOfSyncWarning managedRule={managedRule} />
                </Grid>
              )}
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <CustomiseManagedRuleForm
                namespace=""
                ruleBookId={ruleBookId}
                ruleId={ruleId}
                inputPath={convertToSchemaPath(managedRule.input_field)}
              />
            </Grid>
          </RuleFieldSchema>
        </DialogContent>
        <DialogActions>
          <Footer onClose={onClose} ruleBookId={ruleBookId} />
        </DialogActions>
      </Form>
    </Formik>
  );
}
